import {action, makeObservable, observable} from 'mobx';

export type ReportingChipKind = 'fields' | 'groups';
export const MAX_REPORTING_USAGE_BYTES = 64 * 1024;
export const MAX_REPORTING_USAGE_ENTRIES = 1000;

type Counts = Record<string, number>;

/** Selection counters only: no labels, report data, or growing event history. */
export default class ReportingChipUsage {
    @observable.ref private counts: Counts = {};
    private storageKey: string | null = null;

    constructor() {
        makeObservable(this);
    }

    @action public initialize(userId: string) {
        this.storageKey = userId ? `reporting-chip-usage:v1:${userId}` : null;
        this.counts = {};
        if (!this.storageKey) return;
        try {
            const raw = window.localStorage.getItem(this.storageKey);
            if (!raw || raw.length * 2 > MAX_REPORTING_USAGE_BYTES) return;
            this.counts = this.boundCounts(JSON.parse(raw));
        } catch {
            // Private browsing, disabled storage, or corrupt data: start in memory.
        }
    }

    @action public record(vertical: string, kind: ReportingChipKind, values: string[]) {
        if (!this.storageKey) return;
        const next = {...this.counts};
        Array.from(new Set(values)).forEach(value => {
            if (typeof value !== 'string' || !value || value.length > 256) return;
            const key = JSON.stringify([vertical, kind, value]);
            next[key] = Math.min((next[key] || 0) + 1, Number.MAX_SAFE_INTEGER);
        });
        this.counts = this.boundCounts(next);
        try {
            // The actual write is the availability check; never fill storage to probe it.
            window.localStorage.setItem(this.storageKey, JSON.stringify(this.counts));
        } catch {
            // Keep ranking in memory if the origin's shared quota is full or blocked.
        }
    }

    public rank<T extends {value: string}>(vertical: string, kind: ReportingChipKind, entries: T[] = []): T[] {
        const count = (value: string) => this.counts[JSON.stringify([vertical, kind, value])] || 0;
        // Stable ties preserve the catalog order. Never mutate metadata or selected chips.
        return entries.slice().sort((a, b) => count(b.value) - count(a.value));
    }

    private boundCounts(value: unknown): Counts {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
        const entries = Object.entries(value).filter(([key, count]) => {
            if (key.length > 1024 || !Number.isSafeInteger(count) || count <= 0) return false;
            try {
                const parts = JSON.parse(key);
                return Array.isArray(parts) && parts.length === 3
                    && parts.every(part => typeof part === 'string' && part.length > 0 && part.length <= 256)
                    && ['fields', 'groups'].includes(parts[1]);
            } catch {
                return false;
            }
        }).sort((a, b) => b[1] - a[1]).slice(0, MAX_REPORTING_USAGE_ENTRIES);
        const counts: Counts = Object.fromEntries(entries);
        // Conservative UTF-16 estimate, including JSON escaping. Evict least-used first.
        while (JSON.stringify(counts).length * 2 > MAX_REPORTING_USAGE_BYTES && entries.length) {
            delete counts[entries.pop()![0]];
        }
        return counts;
    }
}
